import React, { createContext, useState, useEffect, useContext } from "react";
import { uploadKiinteistoImage } from "../api/api";
import { Modal, Button, Image } from "react-bootstrap";
import { get, set, del, keys } from "idb-keyval";
import ToastMessage from "../components/ToastMessage";

export const ImageQueueContext = createContext({});

const FAILED_IMAGE_KEY_PREFIX = "failed-";
const SYSTEM_USER_ID = 15;

export const ImageQueueProvider = ({ children }) => {
  const [failedImages, setFailedImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", bg: "success" });
  const showToast = (message, bg = "success") => setToast({ show: true, message, bg });

  // ---------------- Base64 Conversion ----------------
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  // ---------------- Add Failed Image ----------------
  const addFailedImage = async (file, category, subCategory, urlKiinteistoId) => {
    try {
      const base64File = await fileToBase64(file);
      const id = crypto.randomUUID();

      const failedImageWithMetadata = {
        id,
        file: base64File, // base64 string
        name: file.name,
        category,
        subCategory,
        urlKiinteistoId,
      };

      // Update state
      setFailedImages((prev) => [...prev, failedImageWithMetadata]);

      // Persist in IndexedDB
      await set(`${FAILED_IMAGE_KEY_PREFIX}${id}`, failedImageWithMetadata);
    } catch (err) {
      console.error("Failed to convert file to base64", err);
      showToast(`Tiedoston "${file.name}" käsittely epäonnistui`, "danger");
    }
  };

  // ---------------- Remove Failed Image ----------------
  const removeFailedImage = async (id) => {
    setFailedImages((prev) => prev.filter((img) => img.id !== id));
    await del(`${FAILED_IMAGE_KEY_PREFIX}${id}`);
  };

  // The grid that shows these images lives in a completely separate
  // ImageBlock instance (this provider is a single global singleton
  // mounted above the router) with its own local state — there's no
  // direct reference to call back into, so broadcast it the same way
  // api.js already does for rate-limit notices (`ktp:ratelimit`).
  const notifyUploaded = (propertyId) => {
    window.dispatchEvent(new CustomEvent("ktp:imageUploaded", { detail: { propertyId } }));
  };

  // ---------------- Retry Upload (single item, no alert/notify — used by
  // both the per-item button and retryAllUploads below, which each decide
  // separately when it's safe to notify) ----------------
  const retrySingle = async (img) => {
    // Convert base64 back to blob for upload
    const blob = await (await fetch(img.file)).blob();
    const file = new File([blob], img.name, { type: blob.type });

    await uploadKiinteistoImage(
      file,
      img.urlKiinteistoId,
      SYSTEM_USER_ID,
      img.category,
      img.subCategory
    );

    await removeFailedImage(img.id);
  };

  const retryUpload = async (img) => {
    try {
      await retrySingle(img);
      notifyUploaded(img.urlKiinteistoId);
      showToast(`Jonossa ollut kuva "${img.name}" ladattiin nyt onnistuneesti`, "success");
    } catch (err) {
      console.error("Upload failed again", err);
      showToast(`Kuvan "${img.name}" lataus epäonnistui edelleen — se pysyy jonossa`, "danger");
    }
  };

  // ---------------- Retry All ----------------
  const retryAllUploads = async () => {
    const queued = failedImages;
    if (!queued.length) return;
    let succeeded = 0;
    // Only notify once per property, after every retry in the batch has
    // settled — notifying per-image here caused ImageBlock to fire off
    // several overlapping loadImages(true) calls, and since that resets
    // imageUrls to {} before re-resolving, a later call's reset could wipe
    // out blob URLs an earlier (still in-flight) call had just resolved,
    // leaving some images broken until a manual refresh.
    const touchedPropertyIds = new Set();
    for (const img of queued) {
      try {
        await retrySingle(img);
        succeeded++;
        touchedPropertyIds.add(img.urlKiinteistoId);
      } catch (err) {
        console.error(`Upload failed again for "${img.name}"`, err);
      }
    }
    touchedPropertyIds.forEach(notifyUploaded);
    if (succeeded === queued.length) {
      showToast(`${succeeded} jonossa ollutta kuvaa ladattiin nyt onnistuneesti`, "success");
    } else {
      showToast(`${succeeded}/${queued.length} jonossa ollutta kuvaa ladattiin onnistuneesti — loput pysyvät jonossa`, "warning");
    }
  };

  // Auto-retry everything once the browser regains connectivity — the
  // "Yritä kaikki uudelleen" button stays as the manual/explicit option.
  useEffect(() => {
    const handleOnline = () => { retryAllUploads(); };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedImages]);

  // ---------------- Load Failed Images from IndexedDB ----------------
  useEffect(() => {
    const loadFailedImages = async () => {
      try {
        const allKeys = await keys();
        const failedKeys = allKeys.filter(
          (key) => typeof key === "string" && key.startsWith(FAILED_IMAGE_KEY_PREFIX)
        );

        const loaded = [];
        for (const key of failedKeys) {
          const img = await get(key);
          if (img) loaded.push(img);
        }

        setFailedImages(loaded);
      } catch (err) {
        console.error("Failed to load failed images from IndexedDB", err);
      }
    };
    loadFailedImages();
  }, []);

  return (
    <ImageQueueContext.Provider
      value={{
        failedImages,
        addFailedImage,
        removeFailedImage,
        retryUpload,
        retryAllUploads,
        openModal: () => setModalOpen(true),
        closeModal: () => setModalOpen(false),
      }}
    >
      {children}

      <ToastMessage show={toast.show} message={toast.message} bg={toast.bg} onClose={() => setToast(t => ({ ...t, show: false }))} />

      {/* Floating indicator for failed images */}
      {failedImages.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 1050,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.3rem",
          }}
        >
          <div
            onClick={() => setModalOpen(true)}
            style={{
              color: "white",
              fontWeight: 600,
              fontSize: "1rem",
              backgroundColor: "#ba3b46",
              padding: "0.3rem 0.5rem",
              borderRadius: "1rem",
              boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 6px 12px rgba(0,0,0,0.3)";
            }}
          >
            Tallentamattomia kuvia!
          </div>

          <Button
            onClick={() => setModalOpen(true)}
            style={{
              borderRadius: "50%",
              width: "2.5rem",
              height: "2.5rem",
              padding: "1.5rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "1.5rem",
              background: "linear-gradient(135deg, #ffb347, #ffcc33)",
              border: "none",
              color: "#333",
              boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            title="Näytä latausjonossa olevat kuvat"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 6px 12px rgba(0,0,0,0.3)";
            }}
          >
            <i className="bi bi-file-earmark-image"></i> {failedImages.length}
          </Button>
        </div>
      )}

      {/* ---------------- Modal ---------------- */}
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Sinulla on tallentamattomia kuvia:</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {failedImages.length === 0 ? (
            <p>Ei epäonnistuneita kuvia.</p>
          ) : (
            <div className="d-flex flex-wrap justify-content-start gap-3" style={{ marginTop: "0.5rem" }}>
              {failedImages.map((img) => (
                <div
                  key={img.id}
                  className="d-flex flex-column align-items-center"
                  style={{
                    flex: "0 0 200px",
                    maxWidth: "150px",
                    marginBottom: "1rem",
                    textAlign: "center",
                  }}
                >
                  <Image
                    src={img.file}
                    thumbnail
                    style={{
                      width: "100%",
                      height: "10rem",
                      objectFit: "cover",
                      borderRadius: "0.5rem",
                      transition: "transform 0.2s",
                    }}
                  />
                  <div className="mt-2 small text-truncate" title={img.name} style={{ width: "100%" }}>
                    {img.name}
                  </div>
                  <div className="d-flex justify-content-center gap-2 mt-2 flex-wrap">
                    <Button size="sm" variant="success" onClick={() => retryUpload(img)}>
                      Yritä uudelleen
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => removeFailedImage(img.id)}>
                      Poista
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {failedImages.length > 0 && (
            <Button variant="success" onClick={retryAllUploads}>
              Yritä kaikki uudelleen
            </Button>
          )}
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Sulje
          </Button>
        </Modal.Footer>
      </Modal>
    </ImageQueueContext.Provider>
  );
};

// ---------------- Hook ----------------
export const useFailedImages = () => {
  const context = useContext(ImageQueueContext);
  if (!context) throw new Error("useFailedImages must be used within an ImageQueueProvider");
  return context;
};